// =====================================================================
// Assessment Center — Auth module: React context.
//
// Pages and components import useAuth() from here — never directly
// from local-auth-provider.ts or users-store.ts. Swapping providers
// later is passing a different `provider` prop where <AuthContextProvider>
// is mounted in src/App.tsx.
// =====================================================================
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type {
  AuthProvider,
  AuthUser,
  InstructorLoginInput,
  InstructorRegisterInput,
  StudentLoginInput,
  StudentRegisterInput,
} from "./types";
import { onSessionChange } from "./session";
import { LocalAuthProvider } from "./local-auth-provider";

interface AuthContextValue {
  user: AuthUser | null;
  studentLogin: (input: StudentLoginInput) => Promise<AuthUser>;
  studentRegister: (input: StudentRegisterInput) => Promise<AuthUser>;
  instructorLogin: (input: InstructorLoginInput) => Promise<AuthUser>;
  instructorRegister: (input: InstructorRegisterInput) => Promise<AuthUser>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const defaultProvider: AuthProvider = new LocalAuthProvider();

export function AuthContextProvider({
  provider = defaultProvider,
  children,
}: {
  provider?: AuthProvider;
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<AuthUser | null>(() => provider.getCurrentUser());

  useEffect(() => onSessionChange(setUser), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      studentLogin: (input) => provider.studentLogin(input),
      studentRegister: (input) => provider.studentRegister(input),
      instructorLogin: (input) => provider.instructorLogin(input),
      instructorRegister: (input) => provider.instructorRegister(input),
      signOut: () => provider.signOut(),
    }),
    [user, provider]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthContextProvider>.");
  return ctx;
}
